import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, Loader2 } from "lucide-react";

/**
 * PO 2026-02 · Pre-Mohit polish.
 *
 * Always-visible Change Password card for authenticated contractors.
 * Consumes the SAME backend endpoint (`POST /api/auth/change-password`)
 * as the existing dashboard `ChangePasswordBanner`. Backend is NOT
 * modified; this component only exposes the pre-existing capability
 * on the Account settings page so a normal contractor can rotate
 * their password without needing the `force_password_change` flag.
 *
 * Behavior:
 *   - Verify current password (server-side, existing bcrypt path).
 *   - Client-side: reject confirm-mismatch before submit.
 *   - Client-side: reject new-password < 8 chars before submit
 *     (mirrors backend rule; backend still authoritative).
 *   - Surface backend `detail` on failure via toast.
 *   - Clear form fields on success; surface success toast.
 *   - No plaintext password logging anywhere.
 */
const ChangePasswordCard = () => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (saving) return;
    if (!current) {
      toast.error("Please enter your current password");
      return;
    }
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (next === current) {
      toast.error("New password must differ from the current one");
      return;
    }
    if (next !== confirm) {
      toast.error("New password and confirmation do not match");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated. You're all set.");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Password change failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      data-testid="account-change-password-card"
      className="jc-card p-5"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="shrink-0 w-9 h-9 rounded-md bg-[var(--jc-blue)]/10 text-[var(--jc-blue)] flex items-center justify-center">
          <KeyRound size={18} />
        </div>
        <div>
          <div className="overline mb-0.5" data-testid="account-change-password-heading">
            Change Password
          </div>
          <p className="text-sm text-[var(--jc-text-soft)]">
            Update the password you use to sign in. You'll stay signed in on this device; the new password takes effect on your next sign-in elsewhere.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="grid sm:grid-cols-3 gap-3 items-end">
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-[var(--jc-text-soft)]">Current Password</span>
          <input
            type="password"
            data-testid="account-change-password-current"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            className="jc-input"
            autoComplete="current-password"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-[var(--jc-text-soft)]">New Password</span>
          <input
            type="password"
            data-testid="account-change-password-new"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            className="jc-input"
            autoComplete="new-password"
            minLength={8}
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-[var(--jc-text-soft)]">Confirm New Password</span>
          <div className="flex items-center gap-2">
            <input
              type="password"
              data-testid="account-change-password-confirm"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="jc-input"
              autoComplete="new-password"
              minLength={8}
            />
            <button
              type="submit"
              disabled={saving}
              data-testid="account-change-password-submit"
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md jc-btn-primary disabled:opacity-60"
            >
              {saving ? <Loader2 className="animate-spin" size={12} /> : "Save"}
            </button>
          </div>
        </label>
      </form>

      <p className="mt-3 text-xs text-[var(--jc-text-soft)]">
        Minimum 8 characters. Your new password must differ from the current one.
      </p>
    </section>
  );
};

export default ChangePasswordCard;
